import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import eye from "./assets/eye.svg";
import "./Login.css";
import { initializeSocket } from "../socket";
import Login from "./funtionsBD/Authservice";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { allpermissions, changelang } from "./funtionsBD/Mainservice";
import { usePermissions } from "../context/Permissioncontext";
import { useBrand } from "../context/BrandContext";
import { syncBrandAfterAuth } from "../utils/brandAuth";
import { resolveLiveTrainingRedirect } from "../hooks/useLiveTrainingRedirect";
import { isMobileDevice } from "../utils/isMobileDevice";
import { HOME_ACTIVE_ROLE_KEY, readActiveHomeRole, defaultAuthenticatedPath } from "../utils/homeRole";
import { markPresencePromptAfterLogin } from "../utils/playerPresencePrompt";
import { clearTechnicalProfilingSnooze } from "../tour/technicalProfilingPrompt";
import { applyAppLanguage, preferredAppLanguage } from "../utils/appLanguages";
import { LegalFooter } from "./legal/LegalFooter";

export const LoginForm = () => {
  const [cpassword, setcPassword] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [load, setload] = useState(false);
  const location = useLocation();

  const { loadPermissions } = usePermissions();
  const { brand, refreshBrand } = useBrand();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get("expired") === "1";
  const lng = localStorage.getItem("i18nextLng") || localStorage.getItem("lng") || "en";
  const { t, i18n } = useTranslation();
  const loginButtonStyle = {
    background: `linear-gradient(135deg, ${brand?.color_primary || "#0877BC"} 0%, ${
      brand?.color_primary_dark || "#065a8f"
    } 100%)`,
    boxShadow: `0 4px 12px rgba(var(--brand-primary-rgb), 0.3)`,
  };

  useEffect(() => {
    if (location.state && location.state.email) {
      setEmail(location.state.email);
    }
  }, [location.state]);

  const intendedPath = (() => {
    const from = location.state?.from;
    const path = from?.pathname;
    if (!path || path === "/login" || !path.startsWith("/")) return null;
    return `${path}${from.search || ""}`;
  })();

  const goAfterAuth = (fallback) => {
    navigate(intendedPath || fallback, { replace: true });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async (e) => {
    setload(true);
    setError("");
    e.preventDefault();
    
    try {
      const response = await Login(email, cpassword, lng);
      setload(false);

      if (response.data.status === "error") {
        setError(response.data.message);
      } else {
        const userData = response.data.data;
        const token = userData.user_token;
        
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(userData));
        markPresencePromptAfterLogin();
        clearTechnicalProfilingSnooze(userData?.user_id);
        try {
          window.dispatchEvent(new Event("smarttt:auth-changed"));
        } catch {
          /* ignore */
        }

        // Align Home role with repaired type (mis-registered Digiping users were type 3 Coach).
        try {
          localStorage.setItem(HOME_ACTIVE_ROLE_KEY, readActiveHomeRole(userData));
        } catch {
          /* ignore */
        }

        // Same-tab login does not fire "storage" — refresh branding explicitly.
        let clubIdHint = userData.club_id || userData.club_details?.club_id;
        await syncBrandAfterAuth(userData, refreshBrand, { clubIdHint });

        // Handle different user types based on response
        if (userData.isSuperAdmin) {
          // Super Admin login
          const permission = await allpermissions();
          loadPermissions(permission.data.data);
          goAfterAuth("/Dashboard");
        } else {
          // Regular user login (Admin, Coach, Player)
          // Apply saved language from backend so UI uses it for this session
          const savedLang = preferredAppLanguage(userData, i18n);
          if (savedLang) {
            applyAppLanguage(i18n, savedLang);
          }

          const socket = initializeSocket(token);
          console.log("initialized");

          socket.on("disconnect", () => {});
          console.log("disconnect");

          loadPermissions(userData.permissions);
          console.log("load");

          console.log(userData.permissions);
          
          // Check if user has admin role (for dual role support)
          const hasAdmin = () => {
            // Check if user has all_role_names from backend
            if (userData.all_role_names && Array.isArray(userData.all_role_names)) {
              return userData.all_role_names.includes("Admin") || userData.all_role_names.includes("Super Admin");
            }
            // Check if user has all_roles (role IDs)
            if (userData.all_roles && Array.isArray(userData.all_roles)) {
              return userData.all_roles.includes("r2") || userData.all_roles.includes("r1");
            }
            // Check hasAdminRole from backend
            if (userData.hasAdminRole) {
              return true;
            }
            // Check permissions as fallback
            if (userData.permissions && Array.isArray(userData.permissions)) {
              const adminPermissions = ["USER_LIST_VIEW", "CREATE_GROUP", "UPDATE_USER_INFO"];
              return adminPermissions.some(perm => 
                userData.permissions.some(p => p.enum_value === perm)
              );
            }
            // Fallback to type_user_id
            return userData.type_user_id === 1 || userData.type_user_id === 2;
          };
          
          const isAdmin = hasAdmin();
          
          // Debug: Log user data to understand the issue
          console.log('🔍 Login - User data:', {
            userId: userData.user_id,
            email: userData.user_email,
            type_user_id: userData.type_user_id,
            isProfileCompleted: userData.isProfileCompleted,
            isProfileCompletedType: typeof userData.isProfileCompleted,
            isAdmin: isAdmin,
            main_purpose_id: userData.main_purpose_id,
            style_id: userData.style_id
          });
          
          // Check if user has completed profiling
          // For players (type_user_id === 4), they need to complete profiling
          // For admins (type_user_id === 1 or 2), they don't need player profiling
          // For coaches (type_user_id === 3), check if they need profiling
          const isPlayer = userData.type_user_id === 4;
          const isHealthPlayer = Number(userData.main_purpose_id) === 1;
          const needsProfiling = isPlayer && (
            userData.isProfileCompleted === false || 
            userData.isProfileCompleted === null || 
            userData.isProfileCompleted === undefined ||
            !userData.main_purpose_id ||
            (!isHealthPlayer && !userData.style_id)
          );
          
          console.log('🔍 Login - Profiling check:', {
            isPlayer,
            needsProfiling,
            isProfileCompleted: userData.isProfileCompleted,
            hasMainPurpose: !!userData.main_purpose_id,
            hasStyle: !!userData.style_id
          });
          
          if (needsProfiling && !isAdmin) {
            console.log('🔍 User needs profiling - redirecting to /profiling');
            navigate("/profiling");
          } else {
            if (isAdmin) {
              console.log('🔍 User with admin access - skipping player profiling');
            } else {
              console.log('🔍 User profile already completed - proceeding to home');
            }
            chngelang(userData);
          }
        }
      }
    } catch (error) {
      setload(false);
      if (error?.code === "account_removed") {
        setError(t("accountRemovedRestoreHint"));
      } else if (error && error.message) {
        setError(error.message);
      } else if (error && typeof error === 'string') {
        setError(error);
      } else {
        setError(t("login_failed"));
      }
    }
  };

  const chngelang = async (data) => {
    try {
      // Use backend-saved language if available, otherwise current selection
      const langToUse = preferredAppLanguage(data, i18n);
      applyAppLanguage(i18n, langToUse);
      const response2 = await changelang(langToUse);
      if (response2?.data?.status === "success") {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        const homeRole = readActiveHomeRole(user);
        const canAutoLive =
          homeRole !== "admin" &&
          (isMobileDevice() || homeRole === "coach" || homeRole === "monitor");
        if (canAutoLive) {
          try {
            const livePath = await resolveLiveTrainingRedirect(
              user,
              user.permissions || []
            );
            if (!intendedPath && livePath) {
              navigate(livePath, { replace: true });
              return;
            }
          } catch (redirectErr) {
            console.warn("Live training redirect skipped:", redirectErr);
          }
        }

        goAfterAuth(defaultAuthenticatedPath(user));
      }
    } catch (error) {
      console.error("Language change failed:", error);
      localStorage.setItem("lng", lng);
      localStorage.setItem("i18nextLng", lng);
      const fallbackUser = JSON.parse(localStorage.getItem("user") || "{}");
      goAfterAuth(defaultAuthenticatedPath(fallbackUser));
    }
  };

  return (
    <div className="login-form-div">
      <form className="login-form" onSubmit={handleLogin}>
        {sessionExpired && (
          <div className="session-expired-message" role="alert">
            {t("session_expired_message", "Your session has expired. Please log in again.")}
          </div>
        )}
        <label className="label" htmlFor="email">
          {t("email")}
        </label>
        <div className="input">
          <input
            id="email"
            name="email"
            autoComplete="username"
            required
            type="email"
            inputMode="email"
            enterKeyHint="next"
            placeholder={t("enteremail")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <label className="label" htmlFor="password">
          {t("password")}
        </label>
        <div className="input">
          <input
            id="password"
            name="password"
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            value={cpassword}
            placeholder={t("enterpassword")}
            onChange={(e) => setcPassword(e.target.value)}
            required
          />
          <button
            type="button"
            className="password-toggle"
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? t("hide_password") : t("show_password")}
            aria-pressed={showPassword}
          >
            <img className={showPassword ? "hide" : ""} src={eye} alt="" />
          </button>
        </div>

        <div className="forgot-password">
          <a href="/forgot-password">{t("forget")}</a>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          type="submit"
          className="login-button"
          disabled={load}
          style={loginButtonStyle}
        >
          {load ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            t("login")
          )}
        </button>
      </form>
      <LegalFooter />
    </div>
  );
};
